import { useRef, useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  useGlobalFrames,
  useGlobalMaterials,
  useGlobalSizes,
  useBranches,
  useShippingZones,
  useSettings,
} from '../hooks/usePublicContent';
import { useCurrency } from '../hooks/useCurrency';
import { api } from '../lib/api';
import type { Branch, GlobalFrame, GlobalMaterial, GlobalSize, ShippingArea } from '../types';
import { SizeGuideButton } from '../components/SizeGuideModal';

const API_URL = import.meta.env.VITE_API_URL as string;

function fmtDims(s: GlobalSize) {
  return `${s.width} x ${s.height} ${s.unit}`;
}

function calcPrice(size: GlobalSize | null, frame: GlobalFrame | null, material: GlobalMaterial | null) {
  return Number(size?.extra_price ?? 0) + Number(frame?.extra_price ?? 0) + Number(material?.extra_price ?? 0);
}

function getFrameColor(name: string): string | null {
  const n = name.toLowerCase();
  if (n.includes('black')) return '#1C1C1A';
  if (n.includes('white')) return '#F0EDE8';
  if (n.includes('gold')) return '#C4A253';
  if (n.includes('oak') || n.includes('natural')) return '#9B7B3A';
  if (n.includes('walnut') || n.includes('dark')) return '#5C3D2E';
  if (n.includes('silver') || n.includes('grey') || n.includes('gray')) return '#A8A8A0';
  if (n.includes('no frame') || n.includes('unframed')) return null;
  return '#7A6145';
}

function getMaterialDesc(name: string) {
  const n = name.toLowerCase();
  if (n.includes('cotton') || n.includes('rag')) return 'Fine art quality, archival cotton base';
  if (n.includes('lustre')) return 'Semi-gloss finish with vivid color depth';
  if (n.includes('metallic')) return 'Reflective finish for a striking look';
  if (n.includes('gloss')) return 'High-gloss surface, rich contrast';
  if (n.includes('matte') || n.includes('matt')) return 'Non-reflective premium surface';
  if (n.includes('fine art')) return 'Museum-grade archival paper';
  if (n.includes('canvas')) return 'Classic artist canvas texture';
  if (n.includes('photo')) return 'Professional photo paper quality';
  return 'Premium print material';
}

function MaterialIcon({ name }: { name: string }) {
  const n = name.toLowerCase();
  if (n.includes('canvas')) return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
      <rect x="3" y="3" width="18" height="18" rx="1" /><path d="M3 9h18M9 21V9" strokeLinecap="round" />
    </svg>
  );
  if (n.includes('metallic') || n.includes('lustre')) return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
      <path d="M12 3l9 4.5-9 4.5-9-4.5L12 3z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12l9 4.5 9-4.5M3 7.5l9 4.5 9-4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
      <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 3.518 4.197 2 7 2c1.97 0 3.23 1.174 5 3 1.77-1.826 3.03-3 5-3 2.803 0 6 1.518 6 5.191 0 4.105-5.409 8.893-11 14.402z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type DeliveryType = 'pickup' | 'delivery';

const STEPS = ['Upload', 'Size', 'Material', 'Frame', 'Delivery', 'Details'];

const FAQS = [
  { q: 'What image quality is recommended?', a: 'For best results upload at least 2000 px on the long edge. At 300 DPI that covers prints up to roughly 17 x 24 cm with no quality loss. Larger prints benefit from higher resolution.' },
  { q: 'How long does printing take?', a: "Standard orders are dispatched within 5-7 business days. We'll notify you by email when your order ships with a tracking number." },
  { q: 'Can I crop or rotate my image?', a: 'Use the rotation tool in the upload area. For specific cropping instructions add a note and our print team will adjust to your requirements.' },
  { q: 'Can I pick up from a branch?', a: "Yes - select Branch Pickup under Fulfilment and choose your nearest gallery. We'll contact you when your print is ready for collection." },
  { q: 'What happens after I submit a request?', a: 'Our team reviews your image and selections, then sends a final quote and proof within 24 hours. Once you confirm, we begin printing.' },
];

// --- Fallback data ---
const FB_MATERIALS: GlobalMaterial[] = [
  { id: -1, name: 'Cotton Rag', extra_price: 0 },
  { id: -2, name: 'Lustre Photo', extra_price: 8 },
  { id: -3, name: 'Metallic', extra_price: 16 },
];
const FB_FRAMES: GlobalFrame[] = [
  { id: -1, name: 'Natural Oak', preview_image: null, extra_price: 0 },
  { id: -2, name: 'Black Timber', preview_image: null, extra_price: 0 },
  { id: -3, name: 'White Timber', preview_image: null, extra_price: 0 },
  { id: -4, name: 'No Frame', preview_image: null, extra_price: -10 },
];

// --- Main component ---
export function CustomPrintPage() {
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [serverError, setServerError] = useState<string | null>(null);

  const [selectedSize, setSelectedSize] = useState<GlobalSize | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<GlobalMaterial | null>(null);
  const [selectedFrame, setSelectedFrame] = useState<GlobalFrame | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [deliveryType, setDeliveryType] = useState<DeliveryType>('delivery');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { data: sizes = [], isLoading: sizesLoading } = useGlobalSizes();
  const { data: materials = [], isLoading: materialsLoading } = useGlobalMaterials();
  const { data: frames = [], isLoading: framesLoading } = useGlobalFrames();
  const { data: branches = [], isLoading: branchesLoading } = useBranches();
  const { data: shippingZones = [] } = useShippingZones();
  const { data: settings } = useSettings();
  const { format } = useCurrency();

  const totalPrice = calcPrice(selectedSize, selectedFrame, selectedMaterial);

  const allCities = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const zone of shippingZones) {
      for (const city of zone.cities) {
        if (!seen.has(city.name)) { seen.add(city.name); out.push(city.name); }
      }
    }
    return out.sort();
  }, [shippingZones]);

  const areasForCity = useMemo((): ShippingArea[] => {
    if (!selectedCity) return [];
    for (const zone of shippingZones) {
      const city = zone.cities.find((c) => c.name === selectedCity);
      if (city) return city.areas;
    }
    return [];
  }, [shippingZones, selectedCity]);

  const selectedArea = useMemo(
    () => areasForCity.find((a) => a.id === selectedAreaId) ?? null,
    [areasForCity, selectedAreaId],
  );

  const hasShippingZones = allCities.length > 0;
  const globalShippingPrice = Number(settings?.shipping?.price ?? 0);
  const freeThreshold = settings?.shipping?.free_threshold != null
    ? Number(settings.shipping.free_threshold)
    : null;

  const effectiveFeeForArea = (fee: number): number => {
    if (fee > 0) return fee;
    if (freeThreshold !== null && totalPrice >= freeThreshold) return 0;
    return globalShippingPrice;
  };

  let shippingFee: number | null;
  if (deliveryType === 'pickup') {
    shippingFee = 0;
  } else if (selectedArea) {
    shippingFee = effectiveFeeForArea(selectedArea.delivery_fee);
  } else if (!hasShippingZones) {
    shippingFee = freeThreshold !== null && totalPrice >= freeThreshold ? 0 : globalShippingPrice;
  } else {
    shippingFee = null;
  }

  const grandTotal = totalPrice + (shippingFee ?? 0);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 240);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const handleFile = (f: File | undefined) => {
    if (!f) return;
    setFile(f);
    setUploading(true);
    setUploadPct(0);
    const tick = setInterval(() => {
      setUploadPct(p => {
        if (p >= 100) { clearInterval(tick); setUploading(false); return 100; }
        return Math.min(100, p + Math.random() * 35);
      });
    }, 100);
    const url = URL.createObjectURL(f);
    setPreview(url);
    setRotation(0); setZoom(1);
    setStatus('idle'); setServerError(null);
    const img = new Image();
    img.onload = () => setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setServerError(null);
    const fd = new FormData();
    fd.append('name', name);
    fd.append('email', email);
    if (phone) fd.append('phone', phone);
    if (selectedSize) fd.append('size_label', selectedSize.label);
    if (selectedMaterial) fd.append('material_name', selectedMaterial.name);
    if (selectedFrame) fd.append('frame_name', selectedFrame.name);
    if (file) fd.append('image', file);
    fd.append('print_price', String(totalPrice));
    fd.append('delivery_type', deliveryType);
    if (deliveryType === 'pickup' && selectedBranch) fd.append('branch_id', String(selectedBranch.id));
    if (deliveryType === 'delivery') {
      fd.append('delivery_address[address_line1]', addressLine1);
      if (addressLine2) fd.append('delivery_address[address_line2]', addressLine2);
      if (selectedCity) fd.append('delivery_address[city]', selectedCity);
      if (selectedArea) fd.append('delivery_address[area]', selectedArea.name);
      if (selectedAreaId) fd.append('shipping_area_id', String(selectedAreaId));
      if (shippingFee !== null) fd.append('shipping_fee', String(shippingFee));
    }
    if (notes) fd.append('notes', notes);
    try {
      await api.post('/custom-print-requests', fd);
      setStatus('sent');
    } catch (err: unknown) {
      setStatus('error');
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      setServerError(data?.message ?? 'Something went wrong. Please try again.');
    }
  };

  const reset = () => {
    setStatus('idle'); setPreview(null); setFile(null);
    setName(''); setEmail(''); setPhone('');
    setAddressLine1(''); setAddressLine2(''); setSelectedCity(''); setSelectedAreaId(null); setNotes('');
    setSelectedSize(null); setSelectedMaterial(null); setSelectedFrame(null);
    setSelectedBranch(null); setRotation(0); setZoom(1); setImgDims(null);
  };

  const deliveryComplete = deliveryType === 'delivery'
    ? !!(addressLine1 && (hasShippingZones ? (selectedCity && selectedAreaId !== null) : selectedCity))
    : !!selectedBranch;

  const stepsDone = [!!file, !!selectedSize, !!selectedMaterial, !!selectedFrame, deliveryComplete, true];
  const activeStep = stepsDone.findIndex(s => !s);
  const canSubmit = !!preview && !!name && !!email && !!phone && deliveryComplete;

  if (status === 'sent') {
    return <SuccessScreen name={name} size={selectedSize} material={selectedMaterial} frame={selectedFrame} onReset={reset} />;
  }

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />

      {/* Sticky config bar */}
      <div className={`fixed left-0 right-0 top-0 z-50 border-b border-stone-light bg-paper/95 shadow-sm backdrop-blur-sm transition-all duration-300 ${scrolled && !!file ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-3 overflow-hidden text-sm">
            {selectedSize && <span className="font-semibold text-ink">{selectedSize.label}</span>}
            {selectedMaterial && <><span className="text-stone-light">·</span><span className="text-stone">{selectedMaterial.name}</span></>}
            {selectedFrame && <><span className="text-stone-light">·</span><span className="text-stone">{selectedFrame.name}</span></>}
            {!selectedSize && <span className="text-stone">Configure your print below</span>}
          </div>
          <span className="shrink-0 font-serif text-xl font-light text-ink">{selectedSize ? format(grandTotal) : 'Select a size'}</span>
        </div>
      </div>

      {/* Hero */}
      <header className="border-b border-stone-light bg-paper">
        <div className="mx-auto max-w-[1400px] px-6 pb-12 pt-14 md:pb-16 md:pt-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-clay">Custom Fine Art Printing</p>
          <h1 className="mt-4 text-balance font-serif text-5xl font-light leading-[1.05] text-ink md:text-6xl lg:text-7xl">
            Frame Your Story.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-stone">
            Upload any photo. Choose your size, material, and frame. We handle the rest - museum-quality archival printing, hand-crafted solid timber frames, delivered to your door.
          </p>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="sticky top-0 z-40 border-b border-stone-light bg-paper/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[1400px] px-6">
          <div className="flex items-center gap-0 overflow-x-auto py-3.5 [scrollbar-width:none]">
            {STEPS.map((step, i) => {
              const done = stepsDone[i] ?? false;
              const active = i === activeStep;
              return (
                <div key={step} className="flex shrink-0 items-center">
                  <div className={`flex items-center gap-2 px-2.5 py-1 transition-colors ${done ? 'text-ink' : active ? 'text-clay' : 'text-stone/40'}`}>
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-all ${done ? 'bg-ink text-cream' : active ? 'border-2 border-clay text-clay' : 'border border-stone-light text-stone/40'}`}>
                      {done ? '✓' : i + 1}
                    </span>
                    <span className="hidden text-[12px] font-medium sm:block">{step}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px w-5 shrink-0 transition-colors ${done ? 'bg-ink/25' : 'bg-stone-light'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main form */}
      <form onSubmit={handleSubmit}>
        <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid gap-10 md:grid-cols-[58fr_42fr] lg:gap-16">

            {/* LEFT: Upload */}
            <div className="md:sticky md:top-24 md:self-start md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:[scrollbar-width:none]">

              {/* Upload card */}
              <div className="overflow-hidden rounded-2xl border border-stone-light bg-white shadow-sm">
                <div
                  onClick={() => !preview && inputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }}
                  onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                  className={`relative overflow-hidden ${preview ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{ aspectRatio: '4/3.2' }}
                >
                  {preview ? (
                    <div className="flex h-full w-full items-center justify-center overflow-hidden bg-stone-light/10">
                      <img
                        src={preview}
                        alt="Your photo"
                        className="h-full w-full object-contain transition-transform duration-300"
                        style={{ transform: `rotate(${rotation}deg) scale(${zoom})`, transformOrigin: 'center' }}
                      />
                      {uploading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink/50 backdrop-blur-sm">
                          <p className="text-sm font-semibold text-cream">Processing image...</p>
                          <div className="mt-4 h-1.5 w-52 overflow-hidden rounded-full bg-cream/20">
                            <div className="h-full rounded-full bg-cream transition-all duration-150" style={{ width: `${uploadPct}%` }} />
                          </div>
                          <p className="mt-2 text-xs text-cream/60">{Math.round(uploadPct)}%</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`flex h-full flex-col items-center justify-center bg-paper transition-colors ${isDragging ? 'bg-clay/5' : 'hover:bg-cream/50'}`}>
                      <div className={`flex h-20 w-20 items-center justify-center rounded-2xl border-2 border-dashed transition-all ${isDragging ? 'scale-110 border-clay bg-clay/10' : 'border-stone-light'}`}>
                        <svg className={`h-9 w-9 transition-colors ${isDragging ? 'text-clay' : 'text-stone'}`} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                      </div>
                      <p className="mt-5 text-base font-semibold text-ink">
                        {isDragging ? 'Drop your photo here' : 'Drag & drop your photo'}
                      </p>
                      <p className="mt-1 text-sm text-stone">or <span className="cursor-pointer text-clay underline underline-offset-2">click to browse</span></p>
                      <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {['JPG', 'PNG', 'HEIC', 'TIFF'].map(f => (
                          <span key={f} className="rounded-full border border-stone-light bg-cream px-3 py-1 text-[11px] font-medium text-stone">{f}</span>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-stone/50">Max 25 MB - min 2000 px on long edge</p>
                    </div>
                  )}
                </div>

                {/* Image controls bar */}
                {preview && !uploading && (
                  <div className="border-t border-stone-light bg-white px-4 py-3">
                    {imgDims && (
                      <div className="mb-2.5 flex items-center gap-2 text-xs text-stone">
                        <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                        </svg>
                        <span>{imgDims.w.toLocaleString()} x {imgDims.h.toLocaleString()} px</span>
                        <span className="text-stone-light">·</span>
                        <span>{file ? (file.size / (1024 * 1024)).toFixed(1) : '0'} MB</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1">
                        <ImgBtn title="Zoom out" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35M8 11h6"/></svg>
                        </ImgBtn>
                        <span className="w-9 text-center text-[11px] font-medium text-stone">{Math.round(zoom * 100)}%</span>
                        <ImgBtn title="Zoom in" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35M11 8v6M8 11h6"/></svg>
                        </ImgBtn>
                        <span className="mx-1 h-4 w-px shrink-0 bg-stone-light" />
                        <ImgBtn title="Rotate left" onClick={() => setRotation(r => (r - 90 + 360) % 360)}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/></svg>
                        </ImgBtn>
                        <ImgBtn title="Rotate right" onClick={() => setRotation(r => (r + 90) % 360)}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3"/></svg>
                        </ImgBtn>
                      </div>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => inputRef.current?.click()} className="text-xs font-medium text-stone transition-colors hover:text-ink">Replace</button>
                        <button type="button" onClick={() => { setPreview(null); setFile(null); setImgDims(null); }} className="text-xs font-medium text-clay transition-colors hover:text-clay-light">Remove</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Selection summary */}
              {(selectedSize || selectedMaterial || selectedFrame) && (
                <div className="mt-5 overflow-hidden rounded-2xl border border-stone-light bg-white shadow-sm">
                  <div className="border-b border-stone-light bg-paper px-5 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone">Your Selection</p>
                  </div>
                  <div className="divide-y divide-stone-light/50 px-5">
                    {selectedSize && (
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-semibold text-ink">{selectedSize.label}</p>
                          <p className="text-xs text-stone">{fmtDims(selectedSize)}</p>
                        </div>
                        <span className="text-sm font-medium text-ink">{format(selectedSize.extra_price ?? 0)}</span>
                      </div>
                    )}
                    {selectedMaterial && (
                      <div className="flex items-center justify-between py-3">
                        <p className="text-sm text-ink">{selectedMaterial.name}</p>
                        <span className="text-sm text-stone">{format(selectedMaterial.extra_price)}</span>
                      </div>
                    )}
                    {selectedFrame && (
                      <div className="flex items-center justify-between py-3">
                        <p className="text-sm text-ink">{selectedFrame.name}</p>
                        <span className="text-sm text-stone">{format(selectedFrame.extra_price)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-3">
                      <p className="text-sm text-stone">Shipping</p>
                      <span className={`text-sm font-medium ${shippingFee === null ? 'text-stone' : shippingFee === 0 ? 'text-moss' : 'text-ink'}`}>
                        {shippingFee === null ? 'TBD' : shippingFee === 0 ? 'Free' : format(shippingFee)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">Estimated Total</p>
                      <span className="font-serif text-2xl font-light text-ink">{selectedSize ? format(grandTotal) : '--'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Trust chips */}
              <div className="mt-5 flex flex-wrap gap-2">
                {['Museum-grade print', 'Solid timber frames', 'Tracked delivery'].map(t => (
                  <span key={t} className="rounded-full border border-stone-light bg-white px-4 py-1.5 text-[11px] font-medium text-stone shadow-sm">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: Configurator */}
            <div className="space-y-4">

              {/* Step 1: Size */}
              <StepCard step={1} title="Print Size" subtitle="Select your dimensions" completed={!!selectedSize}>
                {sizesLoading ? <SkelGrid n={4} /> : sizes.length === 0 ? (
                  <p className="text-sm text-stone">No sizes configured yet.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {sizes.map((s, i) => {
                      const inactive = s.is_active === false;
                      const sel = selectedSize?.id === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          disabled={inactive}
                          onClick={() => !inactive && setSelectedSize(sel ? null : s)}
                          className={`relative rounded-xl border-2 px-3 py-4 text-left transition-all duration-150 ${
                            inactive ? 'cursor-not-allowed border-stone-light/50 opacity-50'
                            : sel ? 'border-clay bg-clay/5 shadow-sm'
                            : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'
                          }`}
                        >
                          {i === 0 && !inactive && <span className="absolute -top-2.5 left-3 rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold text-cream">Popular</span>}
                          {i === 1 && !inactive && <span className="absolute -top-2.5 left-3 rounded-full bg-clay px-2 py-0.5 text-[10px] font-bold text-cream">Best value</span>}
                          {sel && <CheckBadge />}
                          <p className="text-sm font-bold text-ink">{s.label}</p>
                          <p className="mt-0.5 text-xs text-stone">{fmtDims(s)}</p>
                          {inactive
                            ? <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-stone/50">Out of stock</p>
                            : <p className={`mt-1.5 text-xs font-semibold ${sel ? 'text-clay' : 'text-clay-light'}`}>{format(s.extra_price ?? 0)}</p>
                          }
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="mt-4">
                  <SizeGuideButton />
                </div>
              </StepCard>

              {/* Step 2: Material */}
              <StepCard step={2} title="Print Material" subtitle="Choose your surface & finish" completed={!!selectedMaterial}>
                {materialsLoading ? <SkelList n={3} /> : materials.length === 0 ? (
                  <FallbackMaterials selected={selectedMaterial} onSelect={setSelectedMaterial} format={format} />
                ) : (
                  <div className="space-y-2">
                    {materials.map(m => {
                      const inactive = m.is_active === false;
                      const sel = selectedMaterial?.id === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          disabled={inactive}
                          onClick={() => !inactive && setSelectedMaterial(sel ? null : m)}
                          className={`flex w-full items-center gap-4 rounded-xl border-2 px-4 py-3.5 text-left transition-all duration-150 ${
                            inactive ? 'cursor-not-allowed border-stone-light/50 opacity-50'
                            : sel ? 'border-clay bg-clay/5'
                            : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'
                          }`}
                        >
                          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${sel ? 'bg-clay/15 text-clay' : 'bg-paper text-stone'}`}>
                            <MaterialIcon name={m.name} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-semibold ${inactive ? 'text-stone/50' : 'text-ink'}`}>{m.name}</p>
                            <p className="mt-0.5 text-xs text-stone">{getMaterialDesc(m.name)}</p>
                            {inactive && <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone/50">Out of stock</p>}
                          </div>
                          {!inactive && (
                            <span className={`shrink-0 text-xs font-semibold ${sel ? 'text-clay' : 'text-stone'}`}>{Number(m.extra_price) > 0 ? format(m.extra_price) : 'Free'}</span>
                          )}
                          {sel && <CheckBadge />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </StepCard>

              {/* Step 3: Frame */}
              <StepCard step={3} title="Frame Style" subtitle="Complete the look with a frame" completed={!!selectedFrame}>
                {framesLoading ? <SkelGrid n={4} tall /> : frames.length === 0 ? (
                  <FallbackFrames selected={selectedFrame} onSelect={setSelectedFrame} format={format} />
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {frames.map(f => {
                      const inactive = f.is_active === false;
                      const sel = selectedFrame?.id === f.id;
                      const color = getFrameColor(f.name);
                      return (
                        <button
                          key={f.id}
                          type="button"
                          disabled={inactive}
                          onClick={() => !inactive && setSelectedFrame(sel ? null : f)}
                          className={`relative overflow-hidden rounded-xl border-2 text-left transition-all duration-150 ${
                            inactive ? 'cursor-not-allowed border-stone-light/50 opacity-50'
                            : sel ? 'border-clay shadow-md'
                            : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'
                          }`}
                        >
                          {sel && <CheckBadge />}
                          {inactive && <span className="absolute right-2 top-2 z-10 rounded-full bg-stone/80 px-2 py-0.5 text-[9px] font-semibold text-cream">Out of stock</span>}
                          {f.preview_image ? (
                            <div className="aspect-square overflow-hidden">
                              <img src={`${API_URL}${f.preview_image}`} alt={f.name} className="h-full w-full object-cover" />
                            </div>
                          ) : (
                            <div className="flex aspect-square items-center justify-center bg-stone-light/10">
                              {color ? (
                                <div className="flex h-[58%] w-[58%] items-center justify-center rounded shadow-md" style={{ backgroundColor: color }}>
                                  <div className="h-[55%] w-[55%] rounded-sm bg-paper/25" />
                                </div>
                              ) : (
                                <div className="flex h-[58%] w-[58%] items-center justify-center rounded border-2 border-dashed border-stone-light/50">
                                  <div className="h-[55%] w-[55%] rounded-sm bg-stone-light/20" />
                                </div>
                              )}
                            </div>
                          )}
                          <div className="px-3 py-2.5">
                            <p className={`text-xs font-semibold leading-snug ${inactive ? 'text-stone/50' : 'text-ink'}`}>{f.name}</p>
                            {!inactive && <p className="mt-0.5 text-[11px] text-stone">{Number(f.extra_price) > 0 ? format(f.extra_price) : 'Free'}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </StepCard>

              {/* Step 4: Delivery */}
              <StepCard step={4} title="Fulfilment" subtitle="How would you like to receive your print?" completed={deliveryComplete}>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { type: 'delivery' as DeliveryType, label: 'Home Delivery', desc: 'Shipped to your door',
                      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg> },
                    { type: 'pickup' as DeliveryType, label: 'Branch Pickup', desc: 'Collect from a store',
                      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" /></svg> },
                  ].map(opt => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => setDeliveryType(opt.type)}
                      className={`flex flex-col items-center gap-2.5 rounded-xl border-2 px-4 py-5 text-center transition-all duration-150 ${
                        deliveryType === opt.type ? 'border-clay bg-clay/5 shadow-sm' : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'
                      }`}
                    >
                      <span className={deliveryType === opt.type ? 'text-clay' : 'text-stone'}>{opt.icon}</span>
                      <div>
                        <p className={`text-sm font-semibold ${deliveryType === opt.type ? 'text-clay' : 'text-ink'}`}>{opt.label}</p>
                        <p className="mt-0.5 text-xs text-stone">{opt.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {deliveryType === 'delivery' && (
                  <div className="mt-4 space-y-3">
                    <input type="text" required placeholder="Address line 1 *" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} className={fldCls} />
                    <input type="text" placeholder="Address line 2 (optional)" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} className={fldCls} />

                    {hasShippingZones ? (
                      <>
                        <select
                          required
                          value={selectedCity}
                          onChange={e => { setSelectedCity(e.target.value); setSelectedAreaId(null); }}
                          className={fldCls}
                        >
                          <option value="">Select city / governorate *</option>
                          {allCities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>

                        {selectedCity && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wider text-stone">Select area *</p>
                            <div className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
                              {areasForCity.map(area => {
                                const displayFee = effectiveFeeForArea(area.delivery_fee);
                                return (
                                  <button
                                    key={area.id}
                                    type="button"
                                    onClick={() => setSelectedAreaId(area.id)}
                                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                                      selectedAreaId === area.id
                                        ? 'border-clay bg-clay/5 text-ink'
                                        : 'border-stone-light bg-white text-stone hover:border-stone'
                                    }`}
                                  >
                                    <span className="font-medium text-ink">{area.name}</span>
                                    <span className={`text-sm font-semibold ${selectedAreaId === area.id ? 'text-clay' : 'text-stone'}`}>
                                      {displayFee === 0 ? 'Free' : format(displayFee)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <input type="text" required placeholder="City *" value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className={fldCls} />
                    )}

                    {shippingFee !== null && (
                      <div className="flex items-center justify-between rounded-xl border border-stone-light bg-paper px-4 py-3 text-sm">
                        <span className="text-stone">Shipping fee</span>
                        <span className={`font-semibold ${shippingFee === 0 ? 'text-moss' : 'text-ink'}`}>
                          {shippingFee === 0 ? 'Free' : format(shippingFee)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {deliveryType === 'pickup' && (
                  <div className="mt-4 space-y-2">
                    {branchesLoading ? (
                      <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 animate-pulse rounded-xl bg-stone-light/30" />)}</div>
                    ) : branches.length === 0 ? (
                      <p className="rounded-xl bg-paper px-4 py-4 text-sm text-stone">No branches available - please choose delivery.</p>
                    ) : branches.map(b => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setSelectedBranch(b.id === selectedBranch?.id ? null : b)}
                        className={`flex w-full items-start gap-4 rounded-xl border-2 px-4 py-3.5 text-left transition-all duration-150 ${
                          selectedBranch?.id === b.id ? 'border-clay bg-clay/5' : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'
                        }`}
                      >
                        <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${selectedBranch?.id === b.id ? 'bg-clay/15 text-clay' : 'bg-paper text-stone'}`}>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-ink">{b.name}</p>
                          {b.city && <p className="text-xs text-stone">{b.city}</p>}
                          {b.address && <p className="text-xs text-stone">{b.address}</p>}
                          {b.working_hours && <p className="mt-1 text-[10px] text-stone/60">{b.working_hours}</p>}
                        </div>
                        <div className="shrink-0 text-right">
                          {b.phone && <p className="text-xs text-stone">{b.phone}</p>}
                          {b.maps_url && (
                            <a href={b.maps_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="mt-1 inline-block text-[10px] font-medium text-clay underline-offset-2 hover:underline">
                              View Map
                            </a>
                          )}
                          {selectedBranch?.id === b.id && <span className="mt-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-clay text-[10px] text-cream ml-auto">✓</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </StepCard>

              {/* Step 5: Contact */}
              <StepCard step={5} title="Your Details" subtitle="How should we contact you?" completed={!!(name && email && phone)}>
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input type="text" required placeholder="Full name" value={name} onChange={e => setName(e.target.value)} className={fldCls} />
                    <input type="tel" required placeholder="Phone number" value={phone} onChange={e => setPhone(e.target.value)} className={fldCls} />
                  </div>
                  <input type="email" required placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} className={fldCls} />
                  <textarea rows={3} placeholder="Notes for our print team - cropping, colour adjustments, orientation... (optional)" value={notes} onChange={e => setNotes(e.target.value)} className={`${fldCls} resize-none`} />
                </div>
              </StepCard>

              {/* Price summary */}
              <div className="overflow-hidden rounded-2xl border border-stone-light bg-white shadow-sm">
                <div className="border-b border-stone-light bg-paper px-6 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone">Price Estimate</p>
                </div>
                <div className="px-6 py-5">
                  <div className="space-y-2.5">
                    {selectedSize
                      ? <PriceLine label={`Size (${selectedSize.label})`} value={format(selectedSize.extra_price ?? 0)} />
                      : <PriceLine label="Size" value="Select a size" accent />
                    }
                    {selectedMaterial
                      ? <PriceLine label={`Material (${selectedMaterial.name})`} value={format(selectedMaterial.extra_price)} />
                      : <PriceLine label="Material" value="Select a material" accent />
                    }
                    {selectedFrame
                      ? <PriceLine label={`Frame (${selectedFrame.name})`} value={format(selectedFrame.extra_price)} />
                      : <PriceLine label="Frame" value="Select a frame" accent />
                    }
                    <PriceLine
                      label={deliveryType === 'pickup' ? 'Shipping (pickup)' : 'Shipping'}
                      value={
                        shippingFee === null
                          ? 'Select area to calculate'
                          : shippingFee === 0
                          ? 'Free'
                          : format(shippingFee)
                      }
                      accent={shippingFee === null}
                    />
                  </div>
                  <div className="mt-5 flex items-baseline justify-between border-t border-stone-light pt-4">
                    <p className="text-sm text-stone">Estimated total</p>
                    <div className="text-right">
                      <p className="font-serif text-3xl font-light text-ink">
                        {selectedSize ? format(grandTotal) : '--'}
                      </p>
                      <p className="text-[10px] text-stone">Final price confirmed by our team</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error */}
              {serverError && (
                <div className="rounded-xl border border-clay/30 bg-clay/5 px-5 py-4">
                  <p className="text-sm text-clay">{serverError}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit || status === 'submitting'}
                className={`w-full rounded-2xl px-6 py-5 text-[15px] font-semibold uppercase tracking-[0.1em] transition-all duration-200 ${
                  canSubmit && status !== 'submitting'
                    ? 'bg-ink text-cream shadow-lg hover:bg-clay hover:shadow-xl active:scale-[0.99]'
                    : 'cursor-not-allowed bg-stone-light text-stone'
                }`}
              >
                {status === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2.5">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-cream border-t-transparent" />
                    Processing...
                  </span>
                ) : canSubmit ? 'Request Your Custom Print' : 'Complete All Steps to Continue'}
              </button>

              {!preview && <p className="text-center text-xs text-stone">Upload your photo on the left to begin.</p>}
              {preview && deliveryType === 'pickup' && !selectedBranch && (
                <p className="text-center text-xs text-stone">Please select a pickup branch above.</p>
              )}
              {preview && deliveryType === 'delivery' && hasShippingZones && !selectedAreaId && (
                <p className="text-center text-xs text-stone">Select your city and area to calculate shipping.</p>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Trust section */}
      <div className="border-t border-stone-light bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6">
          <p className="mb-10 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-stone">Why Galleria</p>
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-5">
            {[
              { title: 'Museum-Quality', desc: 'Archival pigment inks built to last 100+ years' },
              { title: 'Hand-Crafted Frames', desc: 'Solid timber, hand-mitred, UV-protective glazing' },
              { title: 'Secure & Private', desc: 'Your images are never shared or stored beyond your order' },
              { title: 'Tracked Delivery', desc: 'Boxed flat and shipped within 5-7 working days' },
              { title: 'Branch Pickup', desc: 'Collect from any of our gallery locations' },
            ].map(item => (
              <div key={item.title} className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-stone-light bg-paper shadow-sm">
                  <svg className="h-6 w-6 text-clay" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-ink">{item.title}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-stone">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="border-t border-stone-light bg-paper py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-clay">Common Questions</p>
          <h2 className="mt-3 font-serif text-4xl font-light text-ink md:text-5xl">Everything you need to know.</h2>
          <div className="mt-10 max-w-3xl divide-y divide-stone-light">
            {FAQS.map((faq, i) => (
              <div key={i} className="py-4">
                <button type="button" onClick={() => setFaqOpen(faqOpen === i ? null : i)} className="flex w-full items-center justify-between gap-6 text-left">
                  <span className={`text-base font-medium transition-colors ${faqOpen === i ? 'text-clay' : 'text-ink'}`}>{faq.q}</span>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-lg font-light transition-all duration-200 ${faqOpen === i ? 'rotate-45 border-clay bg-clay/10 text-clay' : 'border-stone-light text-stone'}`}>+</span>
                </button>
                {faqOpen === i && (
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// --- Shared field class ---
const fldCls = 'w-full rounded-xl border border-stone-light bg-white px-4 py-3.5 text-sm text-ink placeholder:text-stone/60 transition-all focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/10';

// --- Sub-components ---

function StepCard({ step, title, subtitle, completed, children }: {
  step: number; title: string; subtitle: string; completed: boolean; children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-stone-light bg-white shadow-sm">
      <div className={`flex items-center gap-4 border-b px-6 py-4 ${completed ? 'border-stone-light/40 bg-paper/40' : 'border-stone-light bg-paper'}`}>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all ${completed ? 'bg-ink text-cream' : 'border-2 border-stone-light text-stone'}`}>
          {completed ? '✓' : step}
        </span>
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">{title}</p>
          <p className="text-xs text-stone">{subtitle}</p>
        </div>
        {completed && <span className="text-xs font-medium text-moss">Done</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function CheckBadge() {
  return (
    <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-clay text-[10px] font-bold text-cream">✓</span>
  );
}

function ImgBtn({ onClick, title, children }: { onClick: () => void; title: string; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={title} className="flex h-7 w-7 items-center justify-center rounded-lg border border-stone-light text-stone transition-colors hover:border-ink hover:text-ink">
      {children}
    </button>
  );
}

function PriceLine({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-stone">{label}</span>
      <span className={`font-medium ${accent ? 'text-moss' : 'text-ink'}`}>{value}</span>
    </div>
  );
}

function SkelGrid({ n, tall }: { n: number; tall?: boolean }) {
  return (
    <div className={`grid gap-3 ${tall ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className={`animate-pulse rounded-xl bg-stone-light/30 ${tall ? 'aspect-[3/4]' : 'h-24'}`} />
      ))}
    </div>
  );
}

function SkelList({ n }: { n: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-xl bg-stone-light/30" />
      ))}
    </div>
  );
}

function FallbackMaterials({ selected, onSelect, format }: {
  selected: GlobalMaterial | null;
  onSelect: (m: GlobalMaterial | null) => void;
  format: (v: number | string) => string;
}) {
  return (
    <div className="space-y-2">
      {FB_MATERIALS.map(m => {
        const sel = selected?.id === m.id;
        return (
          <button key={m.id} type="button" onClick={() => onSelect(sel ? null : m)}
            className={`flex w-full items-center gap-4 rounded-xl border-2 px-4 py-3.5 text-left transition-all ${sel ? 'border-clay bg-clay/5' : 'border-stone-light bg-white hover:border-stone'}`}>
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${sel ? 'bg-clay/15 text-clay' : 'bg-paper text-stone'}`}><MaterialIcon name={m.name} /></span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink">{m.name}</p>
              <p className="text-xs text-stone">{getMaterialDesc(m.name)}</p>
            </div>
            <span className={`text-xs font-semibold ${sel ? 'text-clay' : 'text-stone'}`}>{Number(m.extra_price) > 0 ? format(m.extra_price) : 'Free'}</span>
            {sel && <CheckBadge />}
          </button>
        );
      })}
    </div>
  );
}

function FallbackFrames({ selected, onSelect, format }: {
  selected: GlobalFrame | null;
  onSelect: (f: GlobalFrame | null) => void;
  format: (v: number | string) => string;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {FB_FRAMES.map(f => {
        const sel = selected?.id === f.id;
        const color = getFrameColor(f.name);
        return (
          <button key={f.id} type="button" onClick={() => onSelect(sel ? null : f)}
            className={`relative overflow-hidden rounded-xl border-2 text-left transition-all ${sel ? 'border-clay shadow-md' : 'border-stone-light bg-white hover:border-stone hover:shadow-sm'}`}>
            {sel && <CheckBadge />}
            <div className="flex aspect-square items-center justify-center bg-stone-light/10">
              {color
                ? <div className="flex h-[58%] w-[58%] items-center justify-center rounded shadow-md" style={{ backgroundColor: color }}><div className="h-[55%] w-[55%] rounded-sm bg-paper/25" /></div>
                : <div className="flex h-[58%] w-[58%] items-center justify-center rounded border-2 border-dashed border-stone-light/50"><div className="h-[55%] w-[55%] rounded-sm bg-stone-light/20" /></div>
              }
            </div>
            <div className="px-3 py-2.5">
              <p className="text-xs font-semibold text-ink">{f.name}</p>
              <p className="mt-0.5 text-[11px] text-stone">{Number(f.extra_price) > 0 ? format(f.extra_price) : 'Free'}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SuccessScreen({ name, size, material, frame, onReset }: {
  name: string; size: GlobalSize | null; material: GlobalMaterial | null; frame: GlobalFrame | null;
  onReset: () => void;
}) {
  const firstName = name.split(' ')[0];
  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-paper py-24">
      <div className="mx-auto max-w-md px-6 text-center">
        <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full border-2 border-moss bg-moss/5 text-3xl text-moss">✓</div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-clay">Request Received</p>
        <h2 className="mt-4 font-serif text-4xl font-light text-ink">{firstName ? `Thank you, ${firstName}.` : 'Thank you.'}</h2>
        <p className="mt-4 text-sm leading-relaxed text-stone">
          We have received your custom print request. Our team will review your image and selections and get back to you within 24 hours with a final quote and proofing image.
        </p>
        {(size || material || frame) && (
          <div className="mt-8 rounded-2xl border border-stone-light bg-white px-6 py-5 text-left shadow-sm">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-stone">Order Summary</p>
            {size && <p className="text-sm text-ink"><span className="text-stone">Size:</span> {size.label} ({fmtDims(size)})</p>}
            {material && <p className="mt-1.5 text-sm text-ink"><span className="text-stone">Material:</span> {material.name}</p>}
            {frame && <p className="mt-1.5 text-sm text-ink"><span className="text-stone">Frame:</span> {frame.name}</p>}
          </div>
        )}
        <button type="button" onClick={onReset}
          className="mt-8 rounded-2xl border-2 border-ink px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-ink transition-all hover:bg-ink hover:text-cream">
          Submit Another Request
        </button>
      </div>
    </div>
  );
}
